import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, Box } from '@mui/material';
import { Control, FieldErrors } from 'react-hook-form';

import LabelledTextField from '../LabelledTextField/LabelledTextField';
import useScreenSize from '../../utils/useScreenSize';
import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import BillingAddressCountrySelect from './BillingAddressCountrySelect';
import { BillingAddressField } from './billingAddressFields';

export interface ChangeBillingAddressPayload {
  firstName: string;
  lastName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  institution?: string;
  institutionEmail?: string;
}

interface BillingAddressFormProps {
  errors: FieldErrors<ChangeBillingAddressPayload>;
  fields: BillingAddressField[];
  register: () => void;
  control: Control<ChangeBillingAddressPayload>;
}

const BillingAddressForm = ({
  errors,
  fields,
  register,
  control
}: BillingAddressFormProps) => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;

  return (
    <Box
      data-testid='billing-address-grid'
      sx={{
        borderRadius: borderRadius.lg,
        border: `1px solid ${palette.elevation.outlined}`,
        p: spaces.md,
        backgroundColor: palette.background.paperElevation2,
        display: 'grid',
        gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
        gap: spaces.md,
      }}
    >
      {fields.map(({ name, label, placeholder, isRequired }) => {
        const labelText = isRequired ? t(label) + ' *' : t(label);
        return name === 'country' ?
          <BillingAddressCountrySelect
            key={name}
            control={control}
            errors={errors}
            labelText={labelText}
            placeholder={placeholder}
          /> :
          <FormControl fullWidth key={label+name}>
            <LabelledTextField
              key={label}
              dataTestId={`${name}-input`}
              name={name}
              label={labelText}
              placeholder={t(placeholder)}
              inputRef={register}
              error={errors[name as keyof ChangeBillingAddressPayload]}
            />
          </FormControl>;
      })}
    </Box>
  );
};

export default BillingAddressForm;
